import React from "react"
import { cloneDeep } from "lodash"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

// styles
import "./StepFormModal.scss"

// Ant Design
import { Form } from "antd"

// apis
import { usePutWorkflowTracker } from "api/hooks/workflows/useWorkflowsApi"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"
import useWorkflowTeam from "components/WorkflowModalForm/utils/useWorkflowTeam"

// component
import { DatePicker, TextInput, Select } from "components-design-system"
import ModalForm from "components/Modal/ModalForm/ModalForm"
import ValidationMessage from "components-design-system/ValidationMessage/ValidationMessage"

dayjs.extend(utc)
dayjs.extend(timezone)

const StepFormModal = ({ state, open, isOnHold, handleForm, invalidateParentQueries }) => {
  const [form] = Form.useForm()

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [stateData, setStateData] = React.useState([])

  // context
  const { workflowTrackerData, workflowId, workflowModel, formatPutReq, getDifferenceBetweenDays, addDays } =
    useWorkflowTracker()
  const { teamOptions } = useWorkflowTeam()

  const putWorkflowTracker = usePutWorkflowTracker({ workflowId: Number(workflowId), workflowModel })

  React.useEffect(() => {
    if (workflowTrackerData?.state_data?.tracker_data) {
      let ogTracker = cloneDeep(workflowTrackerData.state_data.tracker_data)

      if (ogTracker) {
        setStateData(ogTracker.slice(1))
      }
    }
  }, [workflowTrackerData?.state_data?.tracker_data, open])

  const stateStartDate = Form.useWatch("start_date", form)
  const stateEndDate = Form.useWatch("end_date", form)

  const currentStateStatus = state?.status || (state?.is_current ? "Current Step" : null)
  const isCompletedOrBypassed = state?.status === "Completed" || state?.status === "Bypassed"
  const isDisabled = isOnHold && !isCompletedOrBypassed
  const isMessageVisible = isDisabled
  const isAssigneeDisabled =
    (!isOnHold && teamOptions?.length === 0) || (isOnHold && (!isCompletedOrBypassed || teamOptions?.length === 0))

  const handleFormCancel = () => {
    form.resetFields()
    handleForm()
  }

  const onStateStartDateChange = (value) => {
    const newExpectedDuration = getDifferenceBetweenDays(stateEndDate, value)
    if (!Number.isFinite(newExpectedDuration) || newExpectedDuration < 0) {
      form.setFieldsValue({ goal_days: 0 })
    } else {
      form.setFieldsValue({ goal_days: newExpectedDuration })
    }
  }

  const onStateEndDateChange = (value) => {
    const newExpectedDuration = getDifferenceBetweenDays(value, stateStartDate)

    if (!Number.isFinite(newExpectedDuration) || newExpectedDuration < 0) {
      form.setFieldsValue({ goal_days: 0 })
    } else {
      form.setFieldsValue({ goal_days: newExpectedDuration })
    }
  }

  const onStateGoalDaysChange = (value) => {
    if (value < 0 || value === null) {
      form.setFieldsValue({ goal_days: 0 })
      if (dayjs(stateStartDate).isValid()) form.setFieldsValue({ end_date: dayjs(stateStartDate) })
      return
    }
    const formattedStartDate = dayjs(stateStartDate)
    const newExpectedEndDate = addDays(formattedStartDate, value)
    if (!newExpectedEndDate.isValid()) return
    form.setFieldsValue({ end_date: newExpectedEndDate })
  }

  const handleChange = (values) => {
    const changedValueKey = Object.keys(values)[0]
    switch (changedValueKey) {
      case "start_date":
        onStateStartDateChange(values.start_date)
        break
      case "end_date":
        onStateEndDateChange(values.end_date)
        break
      case "goal_days":
        onStateGoalDaysChange(values.goal_days)
        break
      default:
        break
    }
  }

  const onSave = (values) => {
    setIsSubmitting(true)
    const req = formatPutReq({
      stateData,
      model: workflowModel,
      workflowTrackerData,
      values,
      state,
      updatedState: true,
    })

    putWorkflowTracker(
      {
        body: req,
      },
      {
        onSuccess: () => {
          invalidateParentQueries()
        },
        onSettled: () => {
          setIsSubmitting(false)
          handleForm()
        },
      }
    )
  }

  return (
    <ModalForm
      modalProps={{
        type: "edit",
        open: open,
        title: `Edit ${state.state_title} Step`,
        subTitle: currentStateStatus && `Status: ${currentStateStatus}`,
        onOk: handleForm,
        onCancel: handleFormCancel,
        width: 600,
        okText: "Save",
        confirmLoading: isSubmitting,
        className: "state-form-modal-container",
        message: isMessageVisible && (
          <ValidationMessage
            type="note"
            value="Unable to edit step while on hold."
          />
        ),
      }}
      formProps={{
        form: form,
        layout: "vertical",
        disabled: isDisabled,
        onValuesChange: handleChange,
        onFinish: onSave,
        initialValues: {
          start_date: state.start_state_date,
          end_date: state.end_state_date,
          goal_days: state.goal_days,
          assignee: state.assigned_to_ids,
        },
      }}
    >
      <div className="edit-workflow-modal-container">
        <div className="edit-state-form-container">
          <div className="edit-state-form-header-container">
            <Form.Item
              label="Start Date"
              name="start_date"
              style={{
                width: "150px",
                minHeight: "78px",
              }}
              allowClear={false}
              rules={[
                {
                  required: true,
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    if (dayjs(value).isAfter(dayjs(stateEndDate))) {
                      return Promise.reject("Start date cannot be after the end date.")
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              dependencies={["end_date", "goal_days"]}
            >
              <DatePicker
                allowClear={false}
                disabled={isDisabled || state.status === "Bypassed"}
              />
            </Form.Item>
            <Form.Item
              label="End Date"
              name="end_date"
              allowClear={false}
              style={{ width: "150px", minHeight: "78px" }}
              rules={[
                {
                  required: true,
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    if (stateStartDate && dayjs(value).isBefore(dayjs(stateStartDate))) {
                      return Promise.reject(new Error("End date cannot be before the start date."))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              dependencies={["start_date", "goal_days"]}
            >
              <DatePicker
                allowClear={false}
                disabled={isDisabled || state.status === "Bypassed"}
              />
            </Form.Item>
            <Form.Item
              label="Duration (Days)"
              name="goal_days"
              style={{ width: "130px", minHeight: "78px" }}
              rules={[
                {
                  required: true,
                  message: "Duration is required",
                },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject("Invalid value: cannot be negative")
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              dependencies={["start_date", "end_date"]}
            >
              <TextInput
                type="number"
                controls={false}
                min={0}
                disabled={isDisabled || state.status === "Bypassed"}
              />
            </Form.Item>
          </div>
          <div className="edit-state-body-container">
            <Form.Item
              label="Assignee"
              name="assignee"
            >
              <Select
                mode="multiple"
                allowClear
                options={teamOptions}
                style={{ width: "416px", marginBottom: "8px" }}
                disabled={isAssigneeDisabled}
              />
              {teamOptions.length === 0 && (
                <ValidationMessage
                  type="note"
                  value={`To make selection, add team members to the ${workflowModel}`}
                />
              )}
            </Form.Item>
          </div>
        </div>
      </div>
    </ModalForm>
  )
}

export default StepFormModal
